import { useState, useMemo, useEffect } from 'react';
import { format, isFuture, isPast } from 'date-fns';
// import './ReservationList.scss';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import ReservationCalendar from '@components/reservation/ReservationCalendar';
import { getDateFormat, getTimeFormat } from '@utils/dateHelper';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Stack } from '@mui/material';
import type { StylistAvailability, BusinessHours } from '@app-types/models/user';

interface ReservationPlan {
  _id: string;
  name: string;
  zh_name?: string;
  [key: string]: unknown;
}

interface ReservationUser {
  _id: string;
  email?: string;
  displayName?: string;
  phone?: string;
  [key: string]: unknown;
}

interface ReservationStaff {
  _id: string;
  displayName?: string;
  [key: string]: unknown;
}

interface ReservationCoupon {
  _id: string;
  value?: number;
  [key: string]: unknown;
}

interface Reservation {
  _id: string;
  customerName?: string;
  start: string;
  end: string;
  status: string;
  planId?: ReservationPlan;
  userId?: ReservationUser;
  staffId: ReservationStaff;
  createdAt?: string;
  remark?: string;
  createdType?: string;
  paymentType?: string;
  couponId?: ReservationCoupon;
  originalAmount?: number;
  discountedAmount?: number;
  deductCredit?: number;
  customerPhone?: string;
  [key: string]: unknown;
}

interface Plan {
  _id: string;
  name: string;
  price?: number;
}

interface Stylist {
  _id: string;
  availability?: StylistAvailability;
  [key: string]: unknown;
}

interface Business {
  businessHours?: BusinessHours;
  [key: string]: unknown;
}

interface ReservationListProps {
  reservations: Reservation[];
  onEdit: (reservation: Reservation) => void;
  onCancel: (reservationId: string) => void;
  onApproval: (reservationId: string) => void;
  onNoShow: (reservationId: string) => void;
  onComplete: (reservationId: string) => void;
  stylists: Stylist[];
  plans: Plan[];
  business: Business;
  refetchData: () => void;
  stylistId: string;
}

const ReservationList = ({ reservations, onEdit, onCancel, onApproval, onNoShow, onComplete, stylists, plans, business, refetchData, stylistId }: ReservationListProps) => {
  const { t } = useTranslation();
  const { filter: routeFilter } = useParams<{ filter?: string }>();
  const navigate = useNavigate();
  const [filter, setFilter] = useState(routeFilter || 'future');
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');


  useEffect(() => {
    if (routeFilter) {
      setFilter(routeFilter);
    }
  }, [routeFilter]);

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    navigate(`/reservations/${stylistId}/${newFilter}`);
  };

  // Calculate counts for each filter independently
  const pendingApprovalCount = reservations.filter(res =>
    res.status === 'pending-for-approval' &&
    (!stylistId || res.staffId._id === stylistId)
  ).length;

  const futureConfirmedCount = reservations.filter(res =>
    isFuture(new Date(res.start)) &&
    res.status === 'confirmed' &&
    (!stylistId || res.staffId._id === stylistId)
  ).length;

  const pastIncompleteCount = reservations.filter(res =>
    isPast(new Date(res.start)) &&
    res.status === 'confirmed' &&
    (!stylistId || res.staffId._id === stylistId)
  ).length;

  const pastCompletedCount = reservations.filter(res =>
    isPast(new Date(res.start)) &&
    (res.status === 'completed' || res.status === 'auto-completed') &&
    (!stylistId || res.staffId._id === stylistId)
  ).length;

  const pastInvalidCount = reservations.filter(res =>
    isPast(new Date(res.start)) &&
    (res.status === 'approval-timeout' || res.status === 'no-show') &&
    (!stylistId || res.staffId._id === stylistId)
  ).length;

  // Filter reservations based on the selected filter
  const filteredReservations = useMemo(() => {
    return reservations.filter(reservation => {
      const matchesDateFilter = (
        (filter === 'pendingApproval' && reservation.status === 'pending-for-approval') ||
        (filter === 'future' && isFuture(new Date(reservation.start)) && reservation.status === 'confirmed') ||
        (filter === 'past' && isPast(new Date(reservation.start))) ||
        (filter === 'pastIncomplete' && isPast(new Date(reservation.start)) && reservation.status === 'confirmed') ||
        (filter === 'pastCompleted' && isPast(new Date(reservation.start)) && (reservation.status === 'completed' || reservation.status === 'auto-completed')) ||
        (filter === 'pastInvalid' && isPast(new Date(reservation.start)) && (reservation.status === 'approval-timeout' || reservation.status === 'no-show'))
      );

      const matchesStylistId = stylistId ? reservation.staffId._id === stylistId : true;

      return matchesDateFilter && matchesStylistId;
    });
  }, [reservations, filter, stylistId]);

  return (
    <>
      <Button variant="contained" onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}>
        {viewMode === 'list' ? t('reservationList.switchToCalendarView') : t('reservationList.switchToListView')}
      </Button>

      {viewMode === 'list' && (
        <Stack direction="row" spacing={2} sx={{ mb: 2, mt: 2 }}>
          <Button
            variant={filter === 'pendingApproval' ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => handleFilterChange('pendingApproval')}
          >
            {t('reservationList.pendingApproval')} ({pendingApprovalCount})
          </Button>
          <Button
            variant={filter === 'future' ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => handleFilterChange('future')}
          >
            {t('reservationList.futureConfirmed')} ({futureConfirmedCount})
          </Button>
          <Button
            variant={filter === 'pastIncomplete' ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => handleFilterChange('pastIncomplete')}
          >
            {t('reservationList.pastIncomplete')} ({pastIncompleteCount})
          </Button>
          <Button
            variant={filter === 'pastCompleted' ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => handleFilterChange('pastCompleted')}
          >
            {t('reservationList.pastCompleted')} ({pastCompletedCount})
          </Button>
          <Button
            variant={filter === 'pastInvalid' ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => handleFilterChange('pastInvalid')}
          >
            {t('reservationList.pastInvalid')} ({pastInvalidCount})
          </Button>
        </Stack>
      )}

      {viewMode === 'list' ? (
        <div className='full-width-scroll-container'>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('reservationList.reservedSlot')}</TableCell>
                  <TableCell>{t('reservationList.plan')}</TableCell>
                  <TableCell>{t('reservationList.stylist')}</TableCell>
                  <TableCell>{t('reservationList.account')}</TableCell>
                  <TableCell>{t('reservationList.customerName')}</TableCell>
                  <TableCell>{t('reservationList.remark')}</TableCell>
                  <TableCell>{t('reservationList.createdType')}</TableCell>
                  <TableCell>{t('reservationList.paymentType')}</TableCell>
                  <TableCell>{t('reservationList.originalAmount')} HKD</TableCell>
                  <TableCell>{t('reservationList.couponValueApplied')} HKD</TableCell>
                  <TableCell>{t('reservationList.discountedAmount')} HKD</TableCell>
                  <TableCell>{t('reservationList.createdTime')}</TableCell>
                  <TableCell>{t('reservationList.status')}</TableCell>
                  <TableCell>{t('reservationList.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReservations.map((reservation) => {
                  const isPastReservation = isPast(new Date(reservation.start));
                  return (
                    <TableRow key={reservation._id}>
                      <TableCell>
                        {getDateFormat(new Date(reservation.start))}
                        <div>
                          {getTimeFormat(reservation.start)} - {getTimeFormat(reservation.end)}
                        </div>
                      </TableCell>
                      <TableCell>{reservation.planId?.name}</TableCell>
                      <TableCell>{reservation.staffId?.displayName}</TableCell>
                      <TableCell>{reservation.userId?.displayName}</TableCell>
                      <TableCell>{reservation.customerName || ''}</TableCell>
                      <TableCell>{reservation.remark}</TableCell>
                      <TableCell>{reservation.createdType}</TableCell>
                      <TableCell>{reservation.paymentType}</TableCell>
                      <TableCell>{reservation?.originalAmount} HKD</TableCell>
                      <TableCell>{reservation.couponId ? `${reservation.couponId?.value} HKD` : t('reservationList.notApplicable')}</TableCell>
                      <TableCell>{reservation.discountedAmount} HKD</TableCell>
                      <TableCell>{reservation.createdAt ? format(new Date(reservation.createdAt), 'yyyy-MM-dd hh:mm') : ''}</TableCell>
                      <TableCell>{reservation.status}</TableCell>
                      <TableCell>
                        {reservation.status === 'pending-for-approval' && (
                          <Button variant="contained" size="small" onClick={() => onApproval(reservation._id)} sx={{ mb: 2 }}>
                            {t('reservationList.approve')}
                          </Button>
                        )}
                        {(!isPastReservation && reservation.status !== "cancelled") && (
                          <>
                            <Button variant="contained" size="small" onClick={() => onEdit(reservation)} sx={{ mb: 2 }}>
                              {t('reservationList.edit')}
                            </Button>
                            <Button variant="contained" color="error" size="small" onClick={() => onCancel(reservation._id)}>
                              {t('reservationList.cancel')}
                            </Button>
                          </>
                        )}
                        {isPastReservation && reservation.status === 'confirmed' && (
                          <>
                            <Button variant="contained" color="warning" size="small" onClick={() => onNoShow(reservation._id)} sx={{ mb: 2 }}>
                              {t('reservationList.customerNoShow')}
                            </Button>
                            <Button variant="contained" color="success" size="small" onClick={() => onComplete(reservation._id)}>
                              {t('reservationList.completed')}
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : (
        <>
          {stylistId && business?.businessHours && (() => {
            const calendarBusiness = { ...business, businessHours: business.businessHours };
            const selectedStylist = stylists.find(s => s._id === stylistId);
            if (!selectedStylist?.availability) return null;
            const calendarStylist = { ...selectedStylist, availability: selectedStylist.availability };
            return (
              <ReservationCalendar
                business={calendarBusiness}
                stylist={calendarStylist}
                plans={plans}
                refetchData={refetchData}
                reservations={reservations.filter(reservation => reservation.staffId._id === stylistId)}
              />
            );
          })()}
        </>
      )}
    </>
  );
};

export default ReservationList;
